import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Login from "./Login";
import Search from "./Search";
import AssignedProfiles from "./AssignedProfiles";
import CreateProfile from "./CreateProfile";
import ProfileDetail from "./Profiledetail";
import AuditLogs from "./AuditLogs";
import UserManagement from "./UserManagement";
import DuplicateMergeHistory from "./DuplicateMergeHistory";
import RecommendationDashboard from "./RecommendationDashboard";
import HrmsAnalytics from "./HrmsAnalytics";
import TaskAssignment from "./TaskAssignment";
import MyProfile from "./MyProfile";
import FutureProspects from "./FutureProspects";
import ProfileAlerts from "./ProfileAlerts";
import ImportExport from "./ImportExport";
import socket from "./socket";
import API, { resetSessionExpiredGuard } from "./api";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { getPrimaryRoleName, hasPermission } from "./rbac";
import {
  getActiveStoredAccount,
  getStoredAccounts,
  getStoredActiveAccountId,
  removeStoredAccount,
  setStoredActiveAccount,
  updateStoredAccountUser,
  upsertStoredAccount,
} from "./accountSession";

const PROFILE_CREATE_FIELD_KEYS = [
  "duplicate_check",
  "basic_details",
  "community",
  "education_career",
  "addresses",
  "contacts",
  "family_relatives",
  "preferences",
  "persona",
  "payments",
  "tags",
  "photos",
  "documents",
  "primary_photo",
  "phone",
  "email",
  "family_details",
];

const BRAND = {
  red: "#D73C2E",
  black: "#252935",
  mix: "#B9343A",
  redSoft: "#fff2ef",
  redSoftAlt: "#fff7f5",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.20)",
  text: "#252935",
  muted: "#6b6f7d",
  whiteGlass: "rgba(255,255,255,0.90)",
};

const GOOGLE_CLIENT_ID =
  "956333367669-8g9nrjrdakbshlhtrhibmve21ih1e5im.apps.googleusercontent.com";

const DEFAULT_APP_ROUTE = {
  view: "search",
  detailReturnView: "search",
  selectedProfileId: null,
  selectedProfileInitialTab: null,
  selectedProfileAlertType: "all",
  createBiodataItemId: null,
  taskAssignmentTab: null,
  taskAssignmentWorkspaceTab: null,
  importExportTab: null,
};

const APP_VIEWS = new Set([
  "search",
  "create",
  "detail",
  "profile-alerts",
  "assigned-profiles",
  "recommendation-dashboard",
  "future-prospects",
  "hrms-analytics",
  "task-assignment",
  "import-export",
  "my-profile",
  "audit",
  "duplicates",
  "users",
]);

function parseAppRouteHash(hashValue = "") {
  if (typeof window === "undefined") return { ...DEFAULT_APP_ROUTE };

  const rawHash = String(hashValue || "").replace(/^#\/?/, "").trim();
  if (!rawHash) return { ...DEFAULT_APP_ROUTE };

  const [rawPath, rawQuery = ""] = rawHash.split("?");
  const segments = rawPath.split("/").map((part) => decodeURIComponent(part || "").trim()).filter(Boolean);
  const params = new URLSearchParams(rawQuery);
  const route = { ...DEFAULT_APP_ROUTE };

  if (segments[0] === "profiles" || segments[0] === "profile" || segments[0] === "detail") {
    const profileId = segments[1] || params.get("id") || null;
    if (!profileId) return { ...DEFAULT_APP_ROUTE };
    route.view = "detail";
    route.selectedProfileId = profileId;
    route.selectedProfileInitialTab = params.get("tab") || null;
    route.detailReturnView = normalizeAppView(params.get("from")) || "search";
    return route;
  }

  route.view = normalizeAppView(segments[0]) || "search";
  route.selectedProfileInitialTab = params.get("tab") || null;
  route.selectedProfileAlertType = params.get("alertType") || "all";
  route.createBiodataItemId = params.get("biodataItem") || null;
  route.taskAssignmentTab = params.get("taskTab") || null;
  route.taskAssignmentWorkspaceTab = params.get("workspaceTab") || null;
  route.importExportTab = params.get("importExportTab") || null;
  route.detailReturnView = normalizeAppView(params.get("from")) || route.view || "search";
  return route;
}

function normalizeAppView(value) {
  const view = String(value || "").trim();
  return APP_VIEWS.has(view) ? view : "";
}

function buildAppRouteHash({
  view,
  detailReturnView,
  selectedProfileId,
  selectedProfileInitialTab,
  selectedProfileAlertType,
  createBiodataItemId,
  taskAssignmentTab,
  taskAssignmentWorkspaceTab,
  importExportTab,
}) {
  const cleanView = normalizeAppView(view) || "search";
  const params = new URLSearchParams();

  if (cleanView === "detail" && selectedProfileId) {
    if (selectedProfileInitialTab) params.set("tab", selectedProfileInitialTab);
    if (detailReturnView && detailReturnView !== "search") params.set("from", detailReturnView);
    const query = params.toString();
    return `#/profiles/${encodeURIComponent(selectedProfileId)}${query ? `?${query}` : ""}`;
  }

  if (cleanView === "detail") return "#/search";

  if (cleanView === "profile-alerts" && selectedProfileAlertType && selectedProfileAlertType !== "all") {
    params.set("alertType", selectedProfileAlertType);
  }

  if (cleanView === "create" && createBiodataItemId) {
    params.set("biodataItem", createBiodataItemId);
  }

  if (cleanView === "task-assignment") {
    if (taskAssignmentTab) params.set("taskTab", taskAssignmentTab);
    if (taskAssignmentWorkspaceTab) params.set("workspaceTab", taskAssignmentWorkspaceTab);
  }

  if (cleanView === "import-export" && importExportTab) {
    params.set("importExportTab", importExportTab);
  }

  const query = params.toString();
  return `#/${cleanView}${query ? `?${query}` : ""}`;
}

function replaceAppRouteHash(nextHash) {
  if (typeof window === "undefined" || !nextHash) return;
  const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
  if (`${window.location.hash || ""}` === nextHash) return;
  window.history.replaceState(null, "", nextUrl);
}

export default function App() {
  const initialRoute = useMemo(() => parseAppRouteHash(window.location.hash), []);
  const initialAccounts = useMemo(() => getStoredAccounts(), []);
  const initialActiveAccount = useMemo(
    () => getActiveStoredAccount(initialAccounts, getStoredActiveAccountId()),
    [initialAccounts]
  );
  const [accounts, setAccounts] = useState(initialAccounts);
  const [activeAccountId, setActiveAccountId] = useState(initialActiveAccount?.id || null);
  const [view, setView] = useState(initialRoute.view);
  const [detailReturnView, setDetailReturnView] = useState(initialRoute.detailReturnView);
  const [selectedProfileId, setSelectedProfileId] = useState(initialRoute.selectedProfileId);
  const [selectedProfileInitialTab, setSelectedProfileInitialTab] = useState(initialRoute.selectedProfileInitialTab);
  const [selectedProfileAlertType, setSelectedProfileAlertType] = useState(initialRoute.selectedProfileAlertType);
  const [createBiodataItemId, setCreateBiodataItemId] = useState(initialRoute.createBiodataItemId);
  const [taskAssignmentTab, setTaskAssignmentTab] = useState(initialRoute.taskAssignmentTab);
  const [taskAssignmentWorkspaceTab, setTaskAssignmentWorkspaceTab] = useState(initialRoute.taskAssignmentWorkspaceTab);
  const [importExportTab, setImportExportTab] = useState(initialRoute.importExportTab);
  const [createProfileFreshStartKey, setCreateProfileFreshStartKey] = useState(0);
  const [sessionMsg, setSessionMsg] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [navDropdownOpen, setNavDropdownOpen] = useState("");
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [roleSwitching, setRoleSwitching] = useState(false);
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const workMenuRef = useRef(null);
  const importExportMenuRef = useRef(null);
  const reportsMenuRef = useRef(null);
  const activeAccount = useMemo(
    () => accounts.find((account) => account.id === activeAccountId) || accounts[0] || null,
    [accounts, activeAccountId]
  );
  const token = activeAccount?.token || "";
  const user = activeAccount?.user || null;

  // ─── Role helpers for new schema ─────────────────────────────
  // New backend may return:
  // user.role_name
  // user.roles = [{ id, name }]
  // user.role_id
  const role = useMemo(() => getPrimaryRoleName(user), [user]);
  const activeRoleId = user?.effective_role_id || user?.active_role_id || user?.role_id || null;
  const switchableRoles = useMemo(
    () => (Array.isArray(user?.roles) ? user.roles : []),
    [user?.roles]
  );
  const currentRole = useMemo(
    () =>
      switchableRoles.find((candidate) => Number(candidate?.id) === Number(activeRoleId || 0)) ||
      switchableRoles[0] ||
      null,
    [switchableRoles, activeRoleId]
  );
  const alternateRoles = useMemo(
    () => switchableRoles.filter((candidate) => Number(candidate?.id) !== Number(activeRoleId || 0)),
    [switchableRoles, activeRoleId]
  );
  const userTypeLabel = user?.user_type_name || user?.user_type?.name || user?.user_type_code || null;
  const canSearch = hasPermission(user, "profiles", "search");
  const canViewProfile = hasPermission(user, "profiles", "view");
  const canViewAlerts = hasPermission(user, "profiles", "view", "alerts");
  const canCreate =
    hasPermission(user, "profiles", "create") ||
    PROFILE_CREATE_FIELD_KEYS.some((fieldKey) => hasPermission(user, "profiles", "create", fieldKey));
  const canAudit = hasPermission(user, "audit", "view");
  const canReports = hasPermission(user, "reports", "view");
  const canImportExport = hasPermission(user, "exports", "create");
  const canHrmsAnalytics = hasPermission(user, "reports", "view", "hrms_analytics");
  const canHrmsAnalyticsRebuild = hasPermission(user, "reports", "rebuild", "hrms_analytics");
  const canBiodataAssignmentsAccess =
    hasPermission(user, "biodata_assignments", "view") ||
    hasPermission(user, "biodata_assignments", "assign") ||
    hasPermission(user, "biodata_assignments", "update") ||
    hasPermission(user, "biodata_assignments", "complete");
  const canTaskAssignments =
    canBiodataAssignmentsAccess ||
    hasPermission(user, "profiles", "assign") ||
    hasPermission(user, "tasks", "view") ||
    hasPermission(user, "tasks", "create") ||
    hasPermission(user, "tasks", "update") ||
    hasPermission(user, "tasks", "complete") ||
    hasPermission(user, "tasks", "reassign");
  const canFutureProspects = hasPermission(user, "future_prospects", "view");
  const canUpdateFutureProspects = hasPermission(user, "future_prospects", "update");
  const canUsers = hasPermission(user, "users", "view") && hasPermission(user, "rbac", "view");
  const canDuplicateHistory = hasPermission(user, "duplicates", "view");
  const canResolveDuplicates = hasPermission(user, "duplicates", "resolve");
  const keepCreateMounted = canCreate && (view === "create" || (view === "detail" && detailReturnView === "create"));
  const routeHash = useMemo(
    () =>
      buildAppRouteHash({
        view,
        detailReturnView,
        selectedProfileId,
        selectedProfileInitialTab,
        selectedProfileAlertType,
        createBiodataItemId,
        taskAssignmentTab,
        taskAssignmentWorkspaceTab,
        importExportTab,
      }),
    [
      view,
      detailReturnView,
      selectedProfileId,
      selectedProfileInitialTab,
      selectedProfileAlertType,
      createBiodataItemId,
      taskAssignmentTab,
      taskAssignmentWorkspaceTab,
      importExportTab,
    ]
  );

  useEffect(() => {
    if (!token) return;
    replaceAppRouteHash(routeHash);
  }, [token, routeHash]);

  useEffect(() => {
    const handleRouteChange = () => {
      const nextRoute = parseAppRouteHash(window.location.hash);
      setView(nextRoute.view);
      setDetailReturnView(nextRoute.detailReturnView);
      setSelectedProfileId(nextRoute.selectedProfileId);
      setSelectedProfileInitialTab(nextRoute.selectedProfileInitialTab);
      setSelectedProfileAlertType(nextRoute.selectedProfileAlertType);
      setCreateBiodataItemId(nextRoute.createBiodataItemId);
      setTaskAssignmentTab(nextRoute.taskAssignmentTab);
      setTaskAssignmentWorkspaceTab(nextRoute.taskAssignmentWorkspaceTab);
      setImportExportTab(nextRoute.importExportTab);
      setMobileOpen(false);
      setAccountMenuOpen(false);
      setRoleMenuOpen(false);
      setNavDropdownOpen("");
    };

    window.addEventListener("hashchange", handleRouteChange);
    return () => window.removeEventListener("hashchange", handleRouteChange);
  }, []);

  // ─── Session expired listener ────────────────────────────────
  useEffect(() => {
    const handler = (e) =>
      setSessionMsg({
        message: e.detail?.message || "Your session has expired. Please login again.",
        accountId: activeAccountId,
      });

    window.addEventListener("session-expired", handler);
    return () => window.removeEventListener("session-expired", handler);
  }, [activeAccountId]);

  useEffect(() => {
    if (!user?.id) return;

    socket.emit("register", user.id);

    const onForceLogout = (data) => {
      setSessionMsg({
        message: data?.message || "Session expired. Logged in from another device.",
        accountId: activeAccountId,
      });
    };

    socket.on("force-logout", onForceLogout);
    return () => socket.off("force-logout", onForceLogout);
  }, [user, activeAccountId]);

  useEffect(() => {
    setRoleMenuOpen(false);
  }, [activeAccountId, activeRoleId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setAccountMenuOpen(false);
        setRoleMenuOpen(false);
      }
      if (
        !workMenuRef.current?.contains(event.target) &&
        !importExportMenuRef.current?.contains(event.target) &&
        !reportsMenuRef.current?.contains(event.target)
      ) {
        setNavDropdownOpen("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const refreshCurrentUser = useCallback(async () => {
    if (!token) return;

    try {
      const res = await API.get("/auth/me");
      const nextUser = res.data?.user;
      if (!nextUser) return;
      const result = updateStoredAccountUser(activeAccountId || nextUser.id, nextUser);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
    } catch (err) {
      console.error("Failed to hydrate current user:", err);
    }
  }, [token, activeAccountId]);

  useEffect(() => {
    if (!token) return;

    let mounted = true;

    const runRefresh = async () => {
      try {
        const res = await API.get("/auth/me");
        const nextUser = res.data?.user;
        if (!mounted || !nextUser) return;
        const result = updateStoredAccountUser(activeAccountId || nextUser.id, nextUser);
        setAccounts(result.accounts);
        setActiveAccountId(result.activeAccountId);
      } catch (err) {
        if (mounted) {
          console.error("Failed to hydrate current user:", err);
        }
      }
    };

    runRefresh();

    return () => {
      mounted = false;
    };
  }, [token, activeAccountId]);

  useEffect(() => {
    if (!token) return undefined;

    const handleAccessRefresh = () => {
      refreshCurrentUser();
    };

    const handleVisibilityRefresh = () => {
      if (document.visibilityState === "visible") {
        refreshCurrentUser();
      }
    };

    window.addEventListener("focus", handleAccessRefresh);
    window.addEventListener("auth-refresh-request", handleAccessRefresh);
    document.addEventListener("visibilitychange", handleVisibilityRefresh);

    return () => {
      window.removeEventListener("focus", handleAccessRefresh);
      window.removeEventListener("auth-refresh-request", handleAccessRefresh);
      document.removeEventListener("visibilitychange", handleVisibilityRefresh);
    };
  }, [token, refreshCurrentUser]);

  // ─── Auth handlers ───────────────────────────────────────────
  const handleLogin = (data) => {
    const result = upsertStoredAccount(data);
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();
    setSessionMsg(null);
    setAddAccountOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setMobileOpen(false);
    setView("search");
    setDetailReturnView("search");
    setSelectedProfileInitialTab(null);
    setSelectedProfileAlertType("all");
    setCreateBiodataItemId(null);
  };

  const performLogout = async () => {
    try {
      if (token) await API.post("/auth/logout");
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      const result = removeStoredAccount(activeAccountId || user?.id);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
      resetSessionExpiredGuard();
      setSessionMsg(null);
      setView("search");
      setDetailReturnView("search");
      setSelectedProfileId(null);
      setSelectedProfileInitialTab(null);
      setSelectedProfileAlertType("all");
      setCreateBiodataItemId(null);
      setAccountMenuOpen(false);
      setLogoutConfirmOpen(false);
      setAddAccountOpen(false);
      setRoleMenuOpen(false);
    }
  };

  const requestLogout = () => {
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setLogoutConfirmOpen(true);
  };

  const handleViewProfile = (id, options = {}) => {
    if (!canViewProfile) return;
    setDetailReturnView(view === "detail" ? detailReturnView : view);
    setSelectedProfileId(id);
    setSelectedProfileInitialTab(
      typeof options?.initialTab === "string" && options.initialTab.trim()
        ? options.initialTab.trim()
        : null
    );
    setView("detail");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  };

  const handleProfileTabChange = useCallback((tabId) => {
    const normalizedTab = typeof tabId === "string" && tabId.trim() ? tabId.trim() : "";
    const nextTab = normalizedTab && normalizedTab !== "overview" ? normalizedTab : null;
    setSelectedProfileInitialTab((current) => (current === nextTab ? current : nextTab));
  }, []);

  const handleBack = () => {
    setSelectedProfileId(null);
    setSelectedProfileInitialTab(null);
    setView(detailReturnView || "search");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
  };

  const handleViewProfileAlerts = (options = {}) => {
    setSelectedProfileAlertType(
      typeof options?.alertType === "string" && options.alertType.trim()
        ? options.alertType.trim()
        : "all"
    );
    changeView("profile-alerts");
  };

  const handleSessionClose = () => {
    const result = removeStoredAccount(sessionMsg?.accountId || activeAccountId || user?.id);
    setSessionMsg(null);
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();
    setView("search");
    setDetailReturnView("search");
    setSelectedProfileId(null);
    setSelectedProfileInitialTab(null);
    setSelectedProfileAlertType("all");
    setCreateBiodataItemId(null);
    setAccountMenuOpen(false);
    setLogoutConfirmOpen(false);
    setAddAccountOpen(false);
    setRoleMenuOpen(false);
  };

  const handleSwitchAccount = (accountId) => {
    if (!accountId || accountId === activeAccountId) {
      setAccountMenuOpen(false);
      setRoleMenuOpen(false);
      return;
    }

    const result = setStoredActiveAccount(accountId);
    setAccounts(result.accounts);
    setActiveAccountId(result.activeAccountId);
    resetSessionExpiredGuard();
    setSessionMsg(null);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setMobileOpen(false);
    setLogoutConfirmOpen(false);
    setAddAccountOpen(false);
    window.location.reload();
  };

  const openAddAccount = () => {
    setAddAccountOpen(true);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setMobileOpen(false);
    setLogoutConfirmOpen(false);
  };

  const handleSwitchRole = async (roleId) => {
    const nextRoleId = Number(roleId) || null;

    if (!nextRoleId || nextRoleId === Number(activeRoleId || 0) || roleSwitching) {
      return;
    }

    setRoleSwitching(true);

    try {
      const res = await API.post("/auth/switch-role", { role_id: nextRoleId });
      const result = upsertStoredAccount(res.data);
      setAccounts(result.accounts);
      setActiveAccountId(result.activeAccountId);
      resetSessionExpiredGuard();
      setSessionMsg(null);
      setView("search");
      setDetailReturnView("search");
      setSelectedProfileId(null);
      setMobileOpen(false);
      setAccountMenuOpen(false);
      setLogoutConfirmOpen(false);
      setAddAccountOpen(false);
      setRoleMenuOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("Failed to switch role:", err);
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to switch role.";
      window.alert(message);
    } finally {
      setRoleSwitching(false);
    }
  };

  const changeView = (nextView) => {
    setView(nextView);
    if (nextView !== "create") {
      setCreateBiodataItemId(null);
    }
    if (nextView !== "task-assignment") {
      setTaskAssignmentTab(null);
      setTaskAssignmentWorkspaceTab(null);
    }
    if (nextView !== "import-export") {
      setImportExportTab(null);
    }
    if (nextView !== "detail") {
      setDetailReturnView(nextView);
      setSelectedProfileInitialTab(null);
    }
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openNewProfile = () => {
    setCreateBiodataItemId(null);
    setCreateProfileFreshStartKey((key) => key + 1);
    changeView("create");
  };

  const openBiodataProfileWorkflow = (biodataItemId) => {
    const nextItemId = String(biodataItemId || "").trim();
    if (!nextItemId) return;
    setCreateBiodataItemId(nextItemId);
    setView("create");
    setDetailReturnView("task-assignment");
    setTaskAssignmentTab("assigned-to-me");
    setTaskAssignmentWorkspaceTab("biodata");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openTaskAssignment = (taskTab = null, workspaceTab = null) => {
    setTaskAssignmentTab(taskTab);
    setTaskAssignmentWorkspaceTab(workspaceTab);
    setView("task-assignment");
    setDetailReturnView("task-assignment");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  const openImportExport = (tab = null) => {
    setImportExportTab(tab);
    setView("import-export");
    setDetailReturnView("import-export");
    setMobileOpen(false);
    setAccountMenuOpen(false);
    setRoleMenuOpen(false);
    setNavDropdownOpen("");
  };

  // ─── Guard: if current view is no longer allowed, reset ──────
  useEffect(() => {
    if (view === "search" && !canSearch) {
      if (canCreate) setView("create");
      else if (canFutureProspects) setView("future-prospects");
      else if (canReports) setView("recommendation-dashboard");
      else if (canHrmsAnalytics) setView("hrms-analytics");
      else if (canImportExport) setView("import-export");
      else if (canDuplicateHistory) setView("duplicates");
      else if (canTaskAssignments) setView("task-assignment");
      else if (canAudit) setView("audit");
      else if (canUsers) setView("users");
    }
    if (view === "create" && !canCreate) setView("search");
    if (view === "assigned-profiles" && !canSearch) setView("search");
    if (view === "recommendation-dashboard" && !canReports) setView("search");
    if (view === "future-prospects" && !canFutureProspects) setView("search");
    if (view === "hrms-analytics" && !canHrmsAnalytics) setView("search");
    if (view === "task-assignment" && !canTaskAssignments) setView("search");
    if (view === "import-export" && !canImportExport) setView("search");
    if (view === "audit" && !canAudit) setView("search");
    if (view === "users" && !canUsers) setView("search");
    if (view === "duplicates" && !canDuplicateHistory) setView("search");
    if (view === "detail" && !canViewProfile) setView(canSearch ? "search" : "create");
    if (view === "my-profile" && !user) setView("search");
  }, [view, user, canSearch, canViewProfile, canCreate, canFutureProspects, canReports, canImportExport, canHrmsAnalytics, canTaskAssignments, canAudit, canUsers, canDuplicateHistory]);

  const appContent = !token ? (
    <Login onLogin={handleLogin} />
  ) : (
    <div style={S.app}>
      <style>{`
        @media (max-width: 980px) {
          .jk-nav-right {
            position: absolute !important;
            top: calc(100% + 10px) !important;
            left: 18px !important;
            right: 18px !important;
            display: none !important;
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 10px !important;
            padding: 14px !important;
            border-radius: 24px !important;
            background: rgba(255,255,255,0.98) !important;
            border: 1px solid ${BRAND.borderStrong} !important;
            box-shadow: 0 24px 70px rgba(185,52,58,0.18) !important;
            backdrop-filter: blur(18px) !important;
          }
          .jk-nav-right-open {
            display: flex !important;
          }
          .jk-mobile-menu-btn {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .jk-nav-divider {
            display: none !important;
          }
          .jk-account-menu-wrap {
            width: 100% !important;
          }
          .jk-account-menu-dropdown {
            position: static !important;
            width: 100% !important;
            min-width: 0 !important;
            margin-top: 10px !important;
          }
          .jk-user-pill {
            width: 100% !important;
            justify-content: flex-start !important;
          }
          .jk-nav-btn {
            width: 100% !important;
            text-align: left !important;
            justify-content: flex-start !important;
          }
          .jk-nav-dropdown-wrap {
            width: 100% !important;
          }
          .jk-nav-dropdown-menu {
            position: static !important;
            width: 100% !important;
            min-width: 0 !important;
            margin-top: 8px !important;
          }
        }
        @media (max-width: 560px) {
          .jk-nav-brand-wrap {
            display: none !important;
          }
          .jk-nav-left {
            min-width: auto !important;
          }
        }
      `}</style>

      {(() => {
        const workItems = [];
        if (canReports) {
          workItems.push({
            key: "recommendation-dashboard",
            label: "Recommendations Dashboard",
            hint: "Profile suggestions and follow-up matches",
            onClick: () => changeView("recommendation-dashboard"),
            active: view === "recommendation-dashboard",
          });
        }
        if (canTaskAssignments) {
          workItems.push({
            key: "task-assignment",
            label: "Tasks",
            hint: "Assignments, queues, and daily work",
            onClick: () => openTaskAssignment(null),
            active: view === "task-assignment",
          });
        }
        const importExportItems = [];
        if (canImportExport) {
          importExportItems.push({
            key: "import-export-import",
            label: "CSV Import",
            hint: "Stage rows and review duplicates",
            onClick: () => openImportExport("import"),
            active: view === "import-export" && importExportTab !== "export",
          });
          importExportItems.push({
            key: "import-export-export",
            label: "Bulk Export",
            hint: "Export profile PDFs and CSV",
            onClick: () => openImportExport("export"),
            active: view === "import-export" && importExportTab === "export",
          });
        }

        const reportItems = [];
        if (canHrmsAnalytics) {
          reportItems.push({
            key: "hrms-analytics",
            label: "HRMS Analytics",
            hint: "Team performance and productivity",
            onClick: () => changeView("hrms-analytics"),
            active: view === "hrms-analytics",
          });
        }
        if (canAudit) {
          reportItems.push({
            key: "audit",
            label: "Audit Logs",
            hint: "System activity and change history",
            onClick: () => changeView("audit"),
            active: view === "audit",
          });
        }

        return (
          <>

      {/* Top Nav */}
      <nav style={S.nav}>
        <div style={S.navInner}>
          <div style={S.navLeft} className="jk-nav-left" onClick={() => changeView(canSearch ? "search" : canCreate ? "create" : canFutureProspects ? "future-prospects" : canReports ? "recommendation-dashboard" : canImportExport ? "import-export" : canDuplicateHistory ? "duplicates" : canTaskAssignments ? "task-assignment" : "users")}>
            <div style={S.logoMark}>JK</div>
            <div className="jk-nav-brand-wrap">
              <div style={S.navBrand}>Jain Kalyanam</div>
              <div style={S.navSub}>CRM Matrimony Workspace</div>
            </div>
          </div>

          <button
            style={S.mobileMenuBtn}
            className="jk-mobile-menu-btn"
            onClick={() => setMobileOpen((v) => !v)}
          >
            ☰
          </button>

          <div
            style={{
              ...S.navRight,
              ...(mobileOpen ? S.navRightOpen : {}),
            }}
            className={`jk-nav-right ${mobileOpen ? "jk-nav-right-open" : ""}`}
          >
            {canSearch && (
              <NavBtn active={view === "search"} onClick={() => changeView("search")}>
                Search
              </NavBtn>
            )}

            {canCreate && (
              <NavBtn active={view === "create"} onClick={openNewProfile}>
                + New Profile
              </NavBtn>
            )}

            {workItems.length > 0 && (
              <NavDropdown
                refProp={workMenuRef}
                label="Work"
                open={navDropdownOpen === "work"}
                active={workItems.some((item) => item.active)}
                onToggle={() => setNavDropdownOpen((current) => (current === "work" ? "" : "work"))}
                items={workItems}
              />
            )}

            {importExportItems.length > 0 && (
              <NavDropdown
                refProp={importExportMenuRef}
                label="Import / Export"
                open={navDropdownOpen === "import-export"}
                active={importExportItems.some((item) => item.active)}
                onToggle={() => setNavDropdownOpen((current) => (current === "import-export" ? "" : "import-export"))}
                items={importExportItems}
              />
            )}

            {canFutureProspects && (
              <NavBtn active={view === "future-prospects"} onClick={() => changeView("future-prospects")}>
                Future Prospects
              </NavBtn>
            )}

            {reportItems.length > 0 && (
              <NavDropdown
                refProp={reportsMenuRef}
                label="Reports"
                open={navDropdownOpen === "reports"}
                active={reportItems.some((item) => item.active)}
                onToggle={() => setNavDropdownOpen((current) => (current === "reports" ? "" : "reports"))}
                items={reportItems}
              />
            )}

            {canDuplicateHistory && (
              <NavBtn active={view === "duplicates"} onClick={() => changeView("duplicates")}>
                Merge History
              </NavBtn>
            )}

            {canUsers && (
              <NavBtn active={view === "users"} onClick={() => changeView("users")}>
                Users
              </NavBtn>
            )}

            <div style={S.accountMenuWrap} className="jk-account-menu-wrap" ref={accountMenuRef}>
              <button
                type="button"
                style={{ ...S.userPill, ...(accountMenuOpen ? S.userPillActive : {}) }}
                className="jk-user-pill"
                onClick={() =>
                  setAccountMenuOpen((open) => {
                    const nextOpen = !open;
                    if (!nextOpen) setRoleMenuOpen(false);
                    return nextOpen;
                  })
                }
              >
                <div style={S.avatar}>
                  {(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}
                </div>
                <div style={S.userMeta}>
                  <div style={S.userName}>
                    {user?.name || user?.email?.split("@")[0] || "User"}
                  </div>
                  <div style={S.roleBadge}>{role}</div>
                  {userTypeLabel && <div style={S.userTypeBadge}>{userTypeLabel}</div>}
                </div>
                <span style={S.userCaret}>{accountMenuOpen ? "▴" : "▾"}</span>
              </button>

              {accountMenuOpen && (
                <div style={S.accountMenu} className="jk-account-menu-dropdown">
                  <div style={S.accountMenuSection}>
                    <div style={S.accountMenuSectionTitle}>Signed In Accounts</div>
                    {accounts.map((account) => {
                      const isCurrent = account.id === activeAccountId;
                      const accountRole = getPrimaryRoleName(account.user);
                      const accountTypeLabel =
                        account.user?.user_type_name ||
                        account.user?.user_type?.name ||
                        account.user?.user_type_code ||
                        null;

                      return (
                        <button
                          key={account.id}
                          type="button"
                          style={{
                            ...S.accountSwitcherItem,
                            ...(isCurrent ? S.accountSwitcherItemActive : {}),
                          }}
                          onClick={() => handleSwitchAccount(account.id)}
                        >
                          <div style={S.accountSwitcherAvatar}>
                            {(account.user?.name || account.user?.email || "U").slice(0, 1).toUpperCase()}
                          </div>
                          <div style={S.accountSwitcherMeta}>
                            <span style={S.accountSwitcherName}>
                              {account.user?.name || account.user?.email?.split("@")[0] || "User"}
                            </span>
                            <span style={S.accountSwitcherEmail}>{account.user?.email || "-"}</span>
                            <span style={S.accountSwitcherSub}>
                              {accountRole}
                              {accountTypeLabel ? ` • ${accountTypeLabel}` : ""}
                            </span>
                          </div>
                          {isCurrent && <span style={S.accountSwitcherChip}>Current</span>}
                        </button>
                      );
                    })}
                  </div>
                  {alternateRoles.length > 0 && (
                    <div style={S.accountMenuSection}>
                      <div style={S.accountMenuSectionTitle}>Switch Role</div>
                      <button
                        type="button"
                        style={{
                          ...S.accountRoleDropdownToggle,
                          ...(roleMenuOpen ? S.accountRoleDropdownToggleOpen : {}),
                        }}
                        onClick={() => setRoleMenuOpen((open) => !open)}
                        disabled={roleSwitching}
                      >
                        <div style={S.accountRoleDropdownValueRow}>
                          <span style={S.accountRoleDropdownValue}>
                            {roleSwitching ? "Switching..." : currentRole?.name || role || "Select role"}
                          </span>
                          <span style={S.accountRoleDropdownCaret}>
                            {roleMenuOpen ? "▴" : "▾"}
                          </span>
                        </div>
                      </button>

                      {roleMenuOpen && (
                        <div style={S.accountRoleDropdownMenu}>
                          {alternateRoles.map((switchRole) => (
                            <button
                              key={switchRole.id}
                              type="button"
                              style={S.accountRoleOption}
                              onClick={() => handleSwitchRole(switchRole.id)}
                              disabled={roleSwitching}
                            >
                              <span style={S.accountRoleOptionName}>
                                {switchRole.name || `Role #${switchRole.id}`}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <button type="button" style={S.accountMenuItem} onClick={openAddAccount}>
                    <span style={S.accountMenuItemTitle}>Add Account</span>
                    <span style={S.accountMenuItemSub}>Sign in to another approved account</span>
                  </button>
                  <button type="button" style={S.accountMenuItem} onClick={() => changeView("my-profile")}>
                    <span style={S.accountMenuItemTitle}>My Profile</span>
                    <span style={S.accountMenuItemSub}>View account details</span>
                  </button>
                  <button type="button" style={{ ...S.accountMenuItem, ...S.accountMenuDanger }} onClick={requestLogout}>
                    <span style={S.accountMenuItemTitle}>Logout Current Account</span>
                    <span style={S.accountMenuItemSub}>End only this web session</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

          </>
        );
      })()}

      {/* Views */}
      <main style={S.body}>
        <div style={S.pageShell}>
          {view === "search" && canSearch && (
            <Search
              onViewProfile={handleViewProfile}
              onViewProfileAlerts={handleViewProfileAlerts}
              onViewAssignedProfiles={() => changeView("assigned-profiles")}
              onViewAssignedTasks={() => openTaskAssignment("assigned-to-me")}
              canViewProfile={canViewProfile}
              user={user}
            />
          )}

          {view === "profile-alerts" && canViewAlerts && (
            <ProfileAlerts
              onBack={() => changeView("search")}
              onViewProfile={handleViewProfile}
              canViewProfile={canViewProfile}
              initialAlertType={selectedProfileAlertType}
            />
          )}

          {view === "assigned-profiles" && canSearch && (
            <AssignedProfiles
              onBack={() => changeView("search")}
              onSwitchToAssignedByMe={() => openTaskAssignment(null, "profiles")}
              onViewProfile={handleViewProfile}
              canViewProfile={canViewProfile}
            />
          )}

          {keepCreateMounted && (
            <div style={{ display: view === "create" ? "block" : "none" }}>
              <CreateProfile
                token={token}
                user={user}
                freshStartKey={createProfileFreshStartKey}
                onDone={() => {
                  if (createBiodataItemId) {
                    setCreateBiodataItemId(null);
                    openTaskAssignment("assigned-to-me", "biodata");
                    return;
                  }
                  setView("search");
                  setDetailReturnView("search");
                }}
                onViewProfile={handleViewProfile}
                workflowContext={createBiodataItemId ? { type: "biodata", itemId: createBiodataItemId } : null}
              />
            </div>
          )}

          {view === "detail" && selectedProfileId && canViewProfile && (
            <ProfileDetail
              profileId={selectedProfileId}
              initialTab={selectedProfileInitialTab}
              token={token}
              onBack={handleBack}
              returnView={detailReturnView}
              onTabChange={handleProfileTabChange}
              onOpenAssignedToMe={() => changeView("assigned-profiles")}
              onOpenAssignedByMe={() => openTaskAssignment(null, "profiles")}
              backLabel={
                detailReturnView === "create"
                  ? "Back to Profile"
                  : detailReturnView === "assigned-profiles"
                    ? "Back to Assigned Profiles"
                    : detailReturnView === "future-prospects"
                      ? "Back to Future Prospects"
                    : "Back to Search"
              }
              user={user}
              onViewProfile={handleViewProfile}
            />
          )}

          {view === "recommendation-dashboard" && canReports && (
            <RecommendationDashboard
              token={token}
              user={user}
              canViewProfile={canViewProfile}
              onViewProfile={handleViewProfile}
            />
          )}

          {view === "future-prospects" && canFutureProspects && (
            <FutureProspects
              user={user}
              onBack={() => changeView("search")}
              onViewProfile={handleViewProfile}
              canViewProfile={canViewProfile}
              canUpdateFutureProspects={canUpdateFutureProspects}
            />
          )}

          {view === "hrms-analytics" && canHrmsAnalytics && <HrmsAnalytics canRefresh={canHrmsAnalyticsRebuild} />}

          {view === "task-assignment" && canTaskAssignments && (
            <TaskAssignment
              token={token}
              user={user}
              onSwitchToAssignedToMe={() => changeView("assigned-profiles")}
              onOpenBiodataWorkflow={openBiodataProfileWorkflow}
              onViewProfile={handleViewProfile}
              forcedTaskTab={taskAssignmentTab}
              forcedWorkspaceTab={taskAssignmentWorkspaceTab}
            />
          )}

          {view === "import-export" && canImportExport && (
            <ImportExport
              token={token}
              user={user}
              forcedTab={importExportTab}
            />
          )}

          {view === "my-profile" && user && <MyProfile user={user} />}

          {view === "audit" && canAudit && <AuditLogs token={token} user={user} />}

          {view === "duplicates" && canDuplicateHistory && (
            <DuplicateMergeHistory token={token} canResolve={canResolveDuplicates} user={user} />
          )}

          {view === "users" && canUsers && <UserManagement token={token} user={user} />}

          {!canSearch && !canCreate && !canFutureProspects && !canReports && !canImportExport && !canTaskAssignments && !canAudit && !canDuplicateHistory && !canUsers && (
            <div style={S.emptyState}>
              <h2 style={S.emptyTitle}>No Access Configured</h2>
              <p style={S.emptyText}>
                Your account is active{user?.user_type_name ? ` under ${user.user_type_name}` : ""}, but no frontend-accessible permissions are currently assigned. Please ask an administrator to map features and policies to your role.
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Session Expired Modal */}
      {sessionMsg && (
        <div style={S.modalBackdrop}>
          <div style={S.modal}>
            <div style={S.modalIcon}>!</div>
            <h3 style={S.modalTitle}>Session Expired</h3>
            <p style={S.modalText}>{sessionMsg?.message || sessionMsg}</p>
            <button style={S.modalBtn} onClick={handleSessionClose}>
              Continue
            </button>
          </div>
        </div>
      )}

      {addAccountOpen && (
        <div style={S.modalBackdrop}>
          <div style={S.accountAddModal}>
            <div style={S.accountAddModalHeader}>
              <div>
                <h3 style={S.modalTitle}>Add Another Account</h3>
                <p style={S.accountAddModalText}>
                  Sign in with another approved account. Your current account will stay available in this browser.
                </p>
              </div>
              <button
                type="button"
                style={S.accountAddCloseBtn}
                onClick={() => setAddAccountOpen(false)}
                aria-label="Close add account"
              >
                ×
              </button>
            </div>
            <Login
              embedded
              onLogin={handleLogin}
              onCancel={() => setAddAccountOpen(false)}
              showHeader={false}
              submitLabel="Add Account"
              eyebrow="Add another account"
              title="Sign in without logging out"
              subtitle="We’ll keep your current session and add the new account to the switcher."
            />
          </div>
        </div>
      )}

      {logoutConfirmOpen && (
        <div style={S.modalBackdrop}>
          <div style={S.modal}>
            <div style={S.modalIcon}>?</div>
            <h3 style={S.modalTitle}>Confirm Logout</h3>
            <p style={S.modalText}>Are you sure you want to log out from this web session?</p>
            <div style={S.modalActions}>
              <button style={S.modalSecondaryBtn} onClick={() => setLogoutConfirmOpen(false)}>
                Cancel
              </button>
              <button style={S.modalDangerBtn} onClick={performLogout}>
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{appContent}</GoogleOAuthProvider>;
}

function NavBtn({ children, active, onClick }) {
  return (
    <button
      className="jk-nav-btn"
      style={{
        ...S.navBtn,
        ...(active ? S.navBtnActive : {}),
      }}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

function NavDropdown({ refProp, label, active, open, onToggle, items = [] }) {
  return (
    <div ref={refProp} style={S.navDropdownWrap} className="jk-nav-dropdown-wrap">
      <button
        type="button"
        className="jk-nav-btn"
        style={{
          ...S.navBtn,
          ...S.navDropdownToggle,
          ...(active ? S.navBtnActive : {}),
          ...(open ? S.navDropdownToggleOpen : {}),
        }}
        onClick={onToggle}
      >
        <span>{label}</span>
        <span style={S.navDropdownCaret}>{open ? "▴" : "▾"}</span>
      </button>

      {open && (
        <div style={S.navDropdownMenu} className="jk-nav-dropdown-menu">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              style={{
                ...S.navDropdownItem,
                ...(item.active ? S.navDropdownItemActive : {}),
              }}
              onClick={item.onClick}
            >
              <span style={S.navDropdownItemTitle}>{item.label}</span>
              {item.hint ? <span style={S.navDropdownItemSub}>{item.hint}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const S = {
  app: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at 6% 0%, rgba(215,60,46,0.12) 0, transparent 30%), radial-gradient(circle at 88% 8%, rgba(185,52,58,0.10) 0, transparent 26%), linear-gradient(135deg, #ffffff 0%, #fff9f7 48%, #fff7f5 100%)",
    fontFamily: "var(--jm-font-body)",
    color: BRAND.text,
  },

  nav: {
    position: "sticky",
    top: 0,
    zIndex: 100,
    padding: "12px 18px",
    backdropFilter: "blur(22px)",
    background: "rgba(255,255,255,0.88)",
    borderBottom: `1px solid ${BRAND.border}`,
    boxShadow: "0 18px 50px rgba(185,52,58,0.08)",
  },

  navInner: {
    maxWidth: 1480,
    margin: "0 auto",
    minHeight: 62,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },

  navLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    minWidth: 245,
    padding: "6px 8px",
    borderRadius: 20,
    transition: "all 0.18s ease",
  },

  logoMark: {
    width: 44,
    height: 44,
    borderRadius: 16,
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    letterSpacing: 0.2,
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 58%, ${BRAND.black} 100%)`,
    boxShadow: "0 16px 36px rgba(185,52,58,0.28)",
  },

  navBrand: {
    fontFamily: "var(--jm-font-heading)",
    fontSize: 22,
    fontWeight: 700,
    color: BRAND.mix,
    lineHeight: 1.1,
    letterSpacing: -0.35,
  },

  navSub: {
    marginTop: 3,
    fontSize: 11,
    color: BRAND.muted,
    fontWeight: 750,
  },

  navRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },

  navRightOpen: {},

  mobileMenuBtn: {
    display: "none",
    border: `1px solid ${BRAND.borderStrong}`,
    background: "rgba(255,255,255,0.95)",
    borderRadius: 14,
    padding: "9px 12px",
    fontSize: 18,
    cursor: "pointer",
    color: BRAND.mix,
    boxShadow: "0 10px 24px rgba(185,52,58,0.08)",
  },

  userPill: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 12px 6px 6px",
    borderRadius: 999,
    background: BRAND.whiteGlass,
    border: `1px solid ${BRAND.border}`,
    boxShadow: "0 12px 30px rgba(185,52,58,0.08)",
    cursor: "pointer",
    textAlign: "left",
  },
  userPillActive: {
    borderColor: BRAND.borderStrong,
    boxShadow: "0 18px 36px rgba(185,52,58,0.14)",
  },

  avatar: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    boxShadow: "0 10px 24px rgba(185,52,58,0.24)",
  },

  userMeta: {
    lineHeight: 1.1,
    flex: 1,
  },

  userName: {
    fontSize: 12,
    color: BRAND.text,
    fontWeight: 950,
    maxWidth: 132,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  roleBadge: {
    marginTop: 3,
    fontSize: 10,
    color: BRAND.mix,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  userTypeBadge: {
    marginTop: 4,
    fontSize: 10,
    color: BRAND.muted,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  userCaret: {
    color: BRAND.mix,
    fontSize: 12,
    fontWeight: 900,
    marginLeft: 2,
  },

  navDivider: {
    width: 1,
    height: 30,
    background: BRAND.borderStrong,
    margin: "0 5px",
  },

  navBtn: {
    padding: "10px 16px",
    background: "transparent",
    border: "1px solid transparent",
    borderRadius: 999,
    color: BRAND.text,
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 13,
    transition: "all 0.18s ease",
    whiteSpace: "nowrap",
  },

  navBtnActive: {
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    color: "#fff",
    boxShadow: "0 14px 30px rgba(185,52,58,0.24)",
    border: "1px solid rgba(255,255,255,0.22)",
  },
  navDropdownWrap: {
    position: "relative",
  },
  navDropdownToggle: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
  },
  navDropdownToggleOpen: {
    borderColor: BRAND.borderStrong,
    boxShadow: "0 16px 34px rgba(185,52,58,0.16)",
  },
  navDropdownCaret: {
    fontSize: 11,
    fontWeight: 900,
    lineHeight: 1,
  },
  navDropdownMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    left: 0,
    minWidth: 250,
    padding: 10,
    borderRadius: 22,
    background: "rgba(255,255,255,0.98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "grid",
    gap: 8,
    zIndex: 20,
  },
  navDropdownItem: {
    border: "1px solid rgba(185,52,58,0.10)",
    background: "#fff",
    borderRadius: 16,
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 4,
    color: BRAND.text,
  },
  navDropdownItemActive: {
    background: "#fff5f2",
    borderColor: BRAND.borderStrong,
    boxShadow: "0 12px 30px rgba(185,52,58,0.10)",
  },
  navDropdownItemTitle: {
    fontSize: 14,
    fontWeight: 900,
  },
  navDropdownItemSub: {
    fontSize: 12,
    color: BRAND.muted,
    fontWeight: 700,
    lineHeight: 1.45,
  },

  accountMenuWrap: {
    position: "relative",
    marginLeft: 8,
  },
  accountMenu: {
    position: "absolute",
    top: "calc(100% + 10px)",
    right: 0,
    minWidth: 240,
    padding: 10,
    borderRadius: 22,
    background: "rgba(255,255,255,0.98)",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 24px 70px rgba(185,52,58,0.18)",
    display: "grid",
    gap: 8,
    zIndex: 20,
  },
  accountMenuSection: {
    display: "grid",
    gap: 8,
  },
  accountMenuSectionTitle: {
    padding: "4px 6px 0",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.1,
    textTransform: "uppercase",
    color: BRAND.mix,
  },
  accountMenuItem: {
    border: "none",
    background: "#fff7f5",
    borderRadius: 16,
    padding: "12px 14px",
    textAlign: "left",
    cursor: "pointer",
    display: "grid",
    gap: 4,
    color: BRAND.text,
  },
  accountMenuDanger: {
    background: "#fff2ef",
    color: BRAND.mix,
  },
  accountRoleDropdownToggle: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff7f5",
    borderRadius: 14,
    padding: "10px 12px",
    textAlign: "left",
    cursor: "pointer",
    display: "block",
    color: BRAND.text,
  },
  accountRoleDropdownToggleOpen: {
    background: "#fff2ef",
    boxShadow: "0 12px 26px rgba(185,52,58,0.10)",
  },
  accountRoleDropdownValueRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    flex: 1,
  },
  accountRoleDropdownValue: {
    fontSize: 14,
    fontWeight: 900,
    color: BRAND.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountRoleDropdownCaret: {
    color: BRAND.mix,
    fontSize: 12,
    fontWeight: 900,
    flexShrink: 0,
  },
  accountRoleDropdownMenu: {
    display: "grid",
    gap: 6,
    marginTop: 6,
  },
  accountRoleOption: {
    border: "1px solid rgba(185,52,58,0.12)",
    background: "#fff",
    borderRadius: 12,
    padding: "9px 12px",
    textAlign: "left",
    cursor: "pointer",
    display: "block",
    color: BRAND.text,
  },
  accountRoleOptionName: {
    fontSize: 13,
    fontWeight: 900,
    color: BRAND.text,
  },
  accountMenuRoleItemActive: {
    background: "#fff1ed",
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 12px 26px rgba(185,52,58,0.10)",
  },
  accountMenuItemTitle: {
    fontSize: 14,
    fontWeight: 900,
  },
  accountMenuItemSub: {
    fontSize: 12,
    color: BRAND.muted,
    fontWeight: 700,
  },
  accountSwitcherItem: {
    border: "1px solid rgba(185,52,58,0.10)",
    background: "#fff",
    borderRadius: 18,
    padding: "11px 12px",
    display: "grid",
    gridTemplateColumns: "38px 1fr auto",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
    textAlign: "left",
  },
  accountSwitcherItemActive: {
    background: "#fff5f2",
    borderColor: BRAND.borderStrong,
    boxShadow: "0 12px 30px rgba(185,52,58,0.10)",
  },
  accountSwitcherAvatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    color: "#fff",
    fontWeight: 950,
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
  },
  accountSwitcherMeta: {
    minWidth: 0,
    display: "grid",
    gap: 2,
  },
  accountSwitcherName: {
    fontSize: 13,
    fontWeight: 900,
    color: BRAND.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherEmail: {
    fontSize: 11,
    color: BRAND.muted,
    fontWeight: 700,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherSub: {
    fontSize: 11,
    color: BRAND.mix,
    fontWeight: 800,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  accountSwitcherChip: {
    fontSize: 10,
    fontWeight: 900,
    color: BRAND.mix,
    background: "#fff",
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 999,
    padding: "5px 8px",
    whiteSpace: "nowrap",
  },

  body: {
    minHeight: "calc(100vh - 86px)",
    padding: "22px 18px 46px",
  },

  pageShell: {
    maxWidth: 1480,
    margin: "0 auto",
  },
  emptyState: {
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    borderRadius: 28,
    padding: 36,
    textAlign: "center",
    boxShadow: "0 18px 54px rgba(185,52,58,0.08)",
  },
  emptyTitle: {
    margin: "0 0 8px",
    color: BRAND.mix,
    fontWeight: 700,
    letterSpacing: -0.5,
    lineHeight: 1.12,
  },
  emptyText: {
    margin: 0,
    color: BRAND.muted,
    lineHeight: 1.6,
  },

  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.62)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
    padding: 20,
    backdropFilter: "blur(10px)",
  },

  modal: {
    background: "#fff",
    padding: 30,
    borderRadius: 28,
    textAlign: "center",
    width: "min(390px, 100%)",
    boxShadow: "0 30px 90px rgba(37,41,53,0.30)",
    border: `1px solid ${BRAND.borderStrong}`,
  },

  modalIcon: {
    width: 52,
    height: 52,
    borderRadius: "50%",
    display: "grid",
    placeItems: "center",
    margin: "0 auto 14px",
    background: BRAND.redSoft,
    color: BRAND.mix,
    fontWeight: 950,
    fontSize: 24,
  },

  modalTitle: {
    margin: "0 0 8px",
    color: BRAND.text,
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: -0.45,
    lineHeight: 1.12,
  },

  modalText: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
    color: BRAND.muted,
  },
  accountAddModal: {
    width: "min(520px, calc(100vw - 32px))",
    maxHeight: "calc(100vh - 48px)",
    overflowY: "auto",
    background: "rgba(255,255,255,0.98)",
    borderRadius: 28,
    padding: 22,
    boxShadow: "0 28px 80px rgba(37,41,53,0.22)",
    border: `1px solid ${BRAND.borderStrong}`,
  },
  accountAddModalHeader: {
    marginBottom: 18,
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 16,
  },
  accountAddModalText: {
    margin: "8px 0 0",
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
    fontWeight: 600,
  },
  accountAddCloseBtn: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    border: `1px solid ${BRAND.border}`,
    background: "#fff7f5",
    color: BRAND.mix,
    fontSize: 22,
    lineHeight: 1,
    fontWeight: 700,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
  },

  modalBtn: {
    marginTop: 20,
    padding: "12px 18px",
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 950,
    width: "100%",
    boxShadow: "0 16px 34px rgba(185,52,58,0.24)",
  },
  modalActions: {
    marginTop: 20,
    display: "flex",
    gap: 10,
  },
  modalSecondaryBtn: {
    flex: 1,
    padding: "12px 18px",
    background: "#fff",
    color: BRAND.text,
    border: `1px solid ${BRAND.borderStrong}`,
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 900,
  },
  modalDangerBtn: {
    flex: 1,
    padding: "12px 18px",
    background: `linear-gradient(135deg, ${BRAND.red} 0%, ${BRAND.mix} 100%)`,
    color: "#fff",
    border: "none",
    borderRadius: 999,
    cursor: "pointer",
    fontWeight: 950,
    boxShadow: "0 16px 34px rgba(185,52,58,0.24)",
  },
};
